import { useRef } from 'react'
import { motion } from 'framer-motion'
import PageLayout from '../components/PageLayout'
import PageHero from '../components/PageHero'
import { useInView } from '../hooks/useInView'

const assets = [
  {
    title: 'Brand logos',
    body: 'Wordmark and icon in black, white, and Oak yellow. SVG and PNG.',
  },
  {
    title: 'Product screenshots',
    body: 'Storefront, Cortex, and Toolkit surfaces at 1x, 2x, and 3x for mobile and web.',
  },
  {
    title: 'Leadership headshots',
    body: 'High-resolution photography of the founding team, cleared for editorial use.',
  },
  {
    title: 'Brand guidelines',
    body: 'Typography, color, tone of voice, and logo clearspace — everything you need.',
  },
]

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.38, 0.005, 0.215, 1] as const },
  },
}

function AssetsSection() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { threshold: 0.2 })

  return (
    <section ref={ref} className="rounded-section bg-white px-6 py-20 md:px-16 md:py-28">
      <div className="mx-auto max-w-content">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-[640px]">
            <span className="text-sm font-semibold uppercase tracking-widest text-gold-500">
              Press kit
            </span>
            <h2 className="mt-4 font-serif text-[14vw] leading-[1] tracking-[-1.5px] md:text-[clamp(32px,5vw,56px)] text-oak-black">
              Everything you need to cover us well.
            </h2>
          </div>
          <a
            href="mailto:press@oakplatforms.com?subject=Press%20kit%20request"
            className="inline-flex items-center gap-3 rounded-pill border border-oak-black/10 bg-oak-black px-6 py-3 text-[15px] font-medium tracking-[-0.3px] text-white transition-opacity hover:opacity-80"
          >
            Request press kit
            <span className="h-6 w-6 rounded-full bg-oak-green" />
          </a>
        </div>
        <motion.div
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
          className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8"
        >
          {assets.map((a) => (
            <motion.div
              key={a.title}
              variants={fadeUp}
              className="rounded-3xl border border-oak-black/5 bg-oak-cream p-8 md:p-10"
            >
              <h3 className="text-[clamp(20px,2.2vw,26px)] font-medium leading-tight tracking-[-0.4px] text-oak-black">
                {a.title}
              </h3>
              <p className="mt-3 text-[clamp(14px,1.3vw,16px)] leading-[1.55] tracking-[-0.2px] text-oak-black/70">
                {a.body}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

function ContactBlock() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { threshold: 0.3 })

  return (
    <section ref={ref} className="bg-oak-black px-6 py-20 md:px-16 md:py-24">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="mx-auto max-w-3xl text-center"
      >
        <span className="text-sm font-semibold uppercase tracking-widest text-oak-yellow">
          Press inquiries
        </span>
        <h2 className="mt-4 font-serif text-[14vw] leading-[1] tracking-[-1.5px] md:text-[clamp(32px,5vw,56px)] text-white">
          We respond within one business day.
        </h2>
        <a
          href="mailto:press@oakplatforms.com"
          className="mt-10 inline-flex items-center gap-3 rounded-pill bg-white px-7 py-4 text-[clamp(16px,1.5vw,20px)] font-medium tracking-[-0.4px] text-oak-black transition-opacity hover:opacity-80"
        >
          press@oakplatforms.com
          <span className="h-7 w-7 rounded-full bg-oak-green" />
        </a>
      </motion.div>
    </section>
  )
}

export default function PressPage() {
  return (
    <PageLayout>
      <PageHero
        eyebrow="Press"
        title="Everything you need to tell the Oak story."
        description="Facts, boilerplate, visuals, and a direct line to us. If you are writing about social commerce and the communities built on it, start here."
      />
      <AssetsSection />
      <ContactBlock />
    </PageLayout>
  )
}
